import axios from 'axios';
import toast from 'react-hot-toast';
import { addToOfflineQueue, getOfflineQueue, clearOfflineQueue } from './utils/offlineQueue';

// Web builds use the same origin. Native builds must set REACT_APP_API_URL to
// the public HTTPS API, for example https://app.example.com/api.
export const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({ baseURL: API_BASE_URL });

// Attach stored JWT on every request
api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) config.headers['Authorization'] = `Bearer ${token}`;
  const workspaceId = localStorage.getItem('active_workspace');
  // An offline request keeps the workspace it was created in when replayed.
  if (!config._offlineWorkspacePinned && token && workspaceId && !config.headers['X-Workspace-Id']) {
    config.headers['X-Workspace-Id'] = workspaceId;
  }
  return config;
});

// Queue mutating requests while offline; surface auth errors globally
api.interceptors.response.use(
  res => res,
  async err => {
    if (!navigator.onLine && err.config?.method !== 'get') {
      await addToOfflineQueue(err.config);
      return Promise.resolve({ data: { queued: true } });
    }
    if (err.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    if (err.response?.status === 403 && err.response?.data?.error === 'workspace_access_revoked') {
      localStorage.removeItem('active_workspace');
      window.dispatchEvent(new Event('workspace:revoked'));
    }
    return Promise.reject(err);
  }
);

window.addEventListener('online', async () => {
  const queue = await getOfflineQueue();
  if (!queue.length) return;

  const failed = [];
  for (const cfg of queue) {
    try {
      await api(cfg);
    } catch (e) {
      failed.push(cfg);
    }
  }

  await clearOfflineQueue();

  if (failed.length === 0) {
    toast.success(`Back online — ${queue.length} change${queue.length > 1 ? 's' : ''} synced`);
  } else {
    toast.error(`${failed.length} change${failed.length > 1 ? 's' : ''} failed to sync. Please retry.`);
    // Re-queue only the ones that failed
    for (const cfg of failed) await addToOfflineQueue(cfg);
  }
});

export default api;
